import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import { useWorkspaceRoles } from "@/hooks/useWorkspaceRoles";
import GeneralSettingsSection from "./GeneralSettingsSection";
import SlaSettingsSection from "./SlaSettingsSection";
import RoutingSettingsSection from "./RoutingSettingsSection";
import CentralRoutingSettingsSection from "./CentralRoutingSettingsSection";
import ChatbotSettingsSection from "./ChatbotSettingsSection";
import NotificationsSettingsSection from "./NotificationsSettingsSection";
import ApiKeysSettingsSection from "./ApiKeysSettingsSection";

const sections = [
  { id: "general", label: "General", icon: "ri-settings-3-line" },
  { id: "routing", label: "Routing", icon: "ri-route-line" },
  { id: "central-routing", label: "Central routing", icon: "ri-git-branch-line" },
  { id: "sla", label: "SLAs", icon: "ri-timer-flash-line" },
  { id: "bot", label: "Chatbot", icon: "ri-robot-line" },
  { id: "notifications", label: "Notifications", icon: "ri-notification-3-line" },
  { id: "api-keys", label: "API Keys", icon: "ri-key-2-line" },
];

function AdminOnlyPlaceholder({ label }) {
  return (
    <div className="mx-auto w-full max-w-6xl">
      <div className="ui-card-static flex flex-col items-center gap-3 !p-10 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 ring-1 ring-slate-200/80 dark:bg-slate-800 dark:ring-slate-700">
          <i className="ri-lock-line text-xl" aria-hidden />
        </div>
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Admin access required</p>
        <p className="max-w-xs text-xs text-slate-400">
          Only workspace admins can view and manage {label} settings. Contact your admin if you need access.
        </p>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { isAdmin, loading: rolesLoading } = useWorkspaceRoles();
  // ?section= lets the sidebar link straight to a section, so a feature named
  // on the pricing page has one obvious destination instead of three clicks.
  const [searchParams] = useSearchParams();
  const requested = searchParams.get("section");
  const [active, setActive] = useState(
    sections.some((s) => s.id === requested) ? requested : "general"
  );

  return (
    <AppLayout>
      <div className="flex h-full min-h-0 overflow-hidden bg-app-canvas">
        <aside className="ui-subnav hidden h-full min-h-0 w-60 md:flex">
          <div className="px-5 pb-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-teal-700 dark:text-teal-400">Workspace</p>
            <h2 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">Settings</h2>
          </div>
          <nav className="flex flex-col gap-0.5 px-3 pt-2" aria-label="Settings sections">
            {sections.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(s.id)}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-all ${
                  active === s.id ? "ui-pill-active" : "ui-pill-idle"
                }`}
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/80 text-slate-500 shadow-sm ring-1 ring-slate-200/60 dark:bg-slate-800/90 dark:text-slate-400 dark:ring-slate-600">
                  <i className={s.icon} aria-hidden />
                </span>
                {s.label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="scrollbar-thin flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto p-6 sm:p-10">
          <div className="mb-4 flex gap-2 overflow-x-auto pb-2 md:hidden">
            {sections.map((s) => (
              <button
                key={`m-${s.id}`}
                type="button"
                onClick={() => setActive(s.id)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ${
                  active === s.id ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-600"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          {active === "general" && (
            rolesLoading ? null : isAdmin ? <GeneralSettingsSection /> : <AdminOnlyPlaceholder label="General" />
          )}

          {active === "sla" && (
            rolesLoading ? null : isAdmin ? <SlaSettingsSection /> : <AdminOnlyPlaceholder label="SLA" />
          )}

          {active === "routing" && (
            rolesLoading ? null : isAdmin ? <RoutingSettingsSection /> : <AdminOnlyPlaceholder label="Routing" />
          )}

          {active === "central-routing" && !rolesLoading && <CentralRoutingSettingsSection />}

          {active === "bot" && (
            rolesLoading ? null : isAdmin ? <ChatbotSettingsSection /> : <AdminOnlyPlaceholder label="Chatbot" />
          )}

          {active === "notifications" && !rolesLoading && !isAdmin && <AdminOnlyPlaceholder label="Notifications" />}

          {active === "notifications" && !rolesLoading && isAdmin && <NotificationsSettingsSection />}

          {active === "api-keys" && (
            rolesLoading ? null : isAdmin ? <ApiKeysSettingsSection /> : <AdminOnlyPlaceholder label="API Keys" />
          )}
        </div>
      </div>
    </AppLayout>
  );
}
